import { useDeleteAssistant } from '../../../hooks/useDeleteAssistant';
import { IAssistantsList, useListAssistants } from '@/hooks/useListAssistants';
import { AlertTriangle, Bot, ChevronDown, ChevronUp, Edit, Loader2, Trash, X } from 'lucide-react';
import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import AddModelForm from '../AddModel/AddModel';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  modelName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  modelName: string;
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    const toastId = toast.loading(`Deleting assistant "${modelName}"...`);

    try {
      await onConfirm();

      toast.success(`Assistant "${modelName}" deleted successfully`, { id: toastId });

      onClose();
    } catch (error) {
      console.error('Error deleting assistant:', error);

      toast.error(`Error deleting assistant "${modelName}". Please try again.`, {
        id: toastId,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
    >
      <div
        className="rounded-lg shadow-lg max-w-md w-full bg-background"
      >
        <div
          className="p-5 border-b border-border flex justify-between items-center"
        >
          <div className="flex items-center text-secondary">
            <AlertTriangle className="mr-2" size={20} />
            <h3 className="text-lg font-medium">Confirm Delete</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity text-muted-foreground"
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <p className="mb-4 text-foreground">
            Are you sure you want to delete the assistant{' '}
            <span className="font-semibold">{modelName}</span>? This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-muted-foreground rounded-md transition-colors text-foreground bg-background hover:bg-muted-foreground/10"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-md transition-colors flex items-center bg-destructive text-destructive-foreground hover:opacity-90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModelsTable = () => {
  const { assistants, fetchAssistants } = useListAssistants({ auto: true, admin: true });
  const { deleteAssistant } = useDeleteAssistant();
  const [expandedRow] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof IAssistantsList>('order');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [itemSelected, setItemSelected] = useState<IAssistantsList | null>(null);

  // Estados para el modal de confirmación
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Función para mostrar el modal de confirmación
  const showDeleteConfirmation = (id: string) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  // Función para eliminar un modelo (ahora solo se llama después de la confirmación)
  const handleDeleteModel = async () => {
    if (deleteId) {
      await deleteAssistant(deleteId);
      await fetchAssistants();
      setIsModalOpen(false);
      setDeleteId(null);
    }
  };

  // Función para cambiar el ordenamiento
  const handleSort = (field: keyof IAssistantsList) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Ordenar modelos
  const sortedModels = [...assistants].sort((a, b) => {
    // Para manejar diferentes tipos de campos
    if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
      const aValue = (a[sortField] as string).toLowerCase();
      const bValue = (b[sortField] as string).toLowerCase();

      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }

    // Para arrays (suggested_prompts)
    if (Array.isArray(a[sortField]) && Array.isArray(b[sortField])) {
      const aValue = (a[sortField] as string[]).length;
      const bValue = (b[sortField] as string[]).length;

      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    }

    return 0;
  });

  const renderSortIndicator = (field: keyof IAssistantsList) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="ml-1" />
    ) : (
      <ChevronDown size={16} className="ml-1" />
    );
  };

  const handleEditAssistant = (id: string) => {
    setItemSelected(assistants.find((assistant) => assistant.id === id) || null);
  };
  // Get the name of the model to delete
  const modelToDelete = deleteId
    ? assistants.find((assistant) => assistant.id === deleteId)?.name || 'this assistant'
    : '';
  const handlerToRefreshList = async () => {
    await fetchAssistants();
  };
  return (
    <div className="w-full">
      {/* Header with title and button to add */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center mb-4">
          <Bot className="mr-3 text-primary" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Manage Assistants
            </h1>
            <p className="text-lg text-muted-foreground">
              Manage AI assistants that are available to users
            </p>
          </div>
        </div>

        {/* Form component with callback to add models */}
        <div className="ml-auto">
          <AddModelFormWrapper
            onAddModel={handlerToRefreshList}
            itemSelected={itemSelected}
            onCancel={() => setItemSelected(null)}
          />
        </div>
      </div>

      {/* Table of models */}
      <div
        className="overflow-x-auto rounded-lg shadow bg-background"
      >
        <table
          className="min-w-full divide-y bg-background"
        >
          <thead className="bg-muted/50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-primary"
              >
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Name {renderSortIndicator('name')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('description')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Description {renderSortIndicator('description')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('suggested_prompts')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Prompts {renderSortIndicator('suggested_prompts')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell"
              >
                <button
                  className="flex items-center focus:outline-none text-primary"
                >
                  Files
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell"
              >
                <button
                  onClick={() => handleSort('created_at')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Created {renderSortIndicator('created_at')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium   uppercase tracking-wider text-primary"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border w-full">
            {sortedModels.length === 0 ? (
              <tr className="w-full">
                <td colSpan={5} className="px-6 py-8 text-center w-full text-foreground">
                  No models have been added yet. Use the "Add Model" button to create one.
                </td>
              </tr>
            ) : (
              sortedModels.map((model) => (
                <Fragment key={model.id}>
                  <tr className="hover:bg-muted/50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">
                            {model.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div
                        className="text-sm truncate max-w-xs text-muted-foreground"
                      >
                        {model.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <span
                        className="px-2 inline-flex text-xs leading-5 font-bold rounded-full bg-primary/10 text-primary"
                      >
                        {model.suggested_prompts.length} prompts
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <span
                        className="px-2 inline-flex text-xs leading-5 font-bold rounded-full bg-primary/10 text-primary"
                      >
                        {model?.files?.length ?? 0} files
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell text-muted-foreground">
                      {new Date(model.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditAssistant(model.id)}
                        className="ml-4 transition-colors text-primary hover:text-primary/80"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(model.id)}
                        className="ml-4 transition-colors text-destructive hover:text-destructive/80"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                  {/* expanded row with details */}
                  {expandedRow === model.id && (
                    <tr className="bg-background">
                      <td colSpan={5} className="px-6 py-4">
                        <div className="mb-4">
                          <h4 className="font-medium mb-1 text-foreground">
                            System Prompt:
                          </h4>
                          <p
                            className="text-sm whitespace-pre-wrap text-muted-foreground"
                          >
                            {model.system_prompt}
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium mb-1 text-foreground">
                            Suggested Prompts:
                          </h4>
                          <ul className="list-disc ml-5 text-sm text-muted-foreground">
                            {model.suggested_prompts.map((prompt, index) => (
                              <li key={index} className="mb-1">
                                {prompt}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal  confirmation or delete   */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteModel}
        modelName={modelToDelete}
      />
    </div>
  );
};

const AddModelFormWrapper = ({
  onAddModel,
  itemSelected,
  onCancel,
}: {
  onAddModel: (model: any) => Promise<void>;
  itemSelected: IAssistantsList | null;
  onCancel: () => void;
}) => {
  const handleFormSubmit = async (formData: any) => {
    await onAddModel(formData);
  };
  return (
    <div className="inline-block">
      <AddModelForm onSubmit={handleFormSubmit} itemSelected={itemSelected} onCancel={onCancel} />
    </div>
  );
};

export default ModelsTable;
